/**
 * AYUNEL 商品页图册保位置(新房 Horizon 4.1.4)—— 同色换尺码时图册不整块替换:不跳回第 1 张、不闪。2026-09-22。
 *
 * 起因(智鹏 2026-09-22「修」):手机商品页同一个颜色里换尺码,图册被整块替换、跳回第 1 张并闪一下。
 * 官方 assets/media-gallery.js 的 #handleProductSelect 对 detail.html 里的新 <media-gallery> 无条件 this.replaceWith。
 * 旧主题(3.5.1)的同名脚本(2026-06-15 Codex 审定)挂的是 3.5 的 `variant:update`;4.1.4 改成标准事件
 * shopify:product:select(event.promise → { detail: { html } }),旧脚本不能原样搬,这里按同一思路重写。
 *
 * 做法(不改官方 JS):
 *   在 document 的 **capture** 阶段先收到事件(官方挂在 section 上、bubble 阶段)⇒ 我们的 event.promise.then 先登记、先执行。
 *   结果回来时,比「页上当前图册」与「新 HTML 里官方要拿去替换的那个 <media-gallery>」的 media-id 序列:
 *   · 完全一致(= 图没变,典型 = 同色换尺码)→ 把新 HTML 里的 <media-gallery> 删掉 ⇒ 官方 querySelector 拿到 null 直接 return,
 *     图册原地不动:停在客人滑到的那张,缩放 / 灯箱 / 电脑端缩略图都还是原来那一套(官方监听也还在,下次换色照常换图);
 *   · 不一致(换颜色 —— 新房按色 alt 过滤,见 snippets/product-media-gallery-content.liquid 的 AYUNEL OVERRIDE 段;
 *     或某尺码配了独立主图)→ 不拦,官方照常整块替换。
 *   不靠「颜色 vs 尺码」的语义判断:直接比 media-id 序列,任何选项改了图都会正确放行。
 *
 * 只管本页主商品的选择器(main 里、data-template-product-match="true"、不在商品卡 / quick-add 里);
 * 换商品(组合商品 newProduct)、同一 section 不止一个图册、序列为空、请求被中止 → 一律不插手,交给官方。
 * 本脚本出错也只是退回官方行为(官方的 then 挂在原 promise 上,不经过我们)。
 *
 * 挂载:snippets/product-media-gallery-content.liquid,<media-gallery> 之前(与 drag-zoom-wrapper.js 同姿势,有图册的页才加载)。
 * 守卫:tests/test_theme_pdp_gallery_keep_position.py(node 假 DOM 把官方原文和本文件一起跑);
 * 前台实测:08-themes/pdp-gallery-keep-src/gallery_keep_walk.py。
 */
import { StandardEvents } from '@shopify/events';

const FLAG = '__ayunelPdpGalleryKeepPosition';

if (!window[FLAG]) {
  window[FLAG] = true;
  document.addEventListener(StandardEvents.productSelect, onProductSelect, { capture: true });
}

function onProductSelect(event) {
  const picker = mainPicker(event.target);
  if (!picker) return;
  const { promise } = event;
  if (!promise || typeof promise.then !== 'function') return;
  // 官方图册监听挂在 closest('.shopify-section, dialog') 上;同一范围里的图册才是会被替换的那个
  const scope = picker.closest('.shopify-section, dialog');
  if (!scope) return;

  promise
    .then((result) => {
      const detail = result && result.detail;
      const html = detail && detail.html;
      if (!html || typeof html.querySelector !== 'function' || detail.newProduct) return;

      const next = html.querySelector('media-gallery'); // 与官方 #handleProductSelect 取的是同一个
      if (!next) return;

      const current = Array.from(scope.querySelectorAll('media-gallery')).filter(
        (gallery) => gallery.closest('.shopify-section, dialog') === scope
      );
      if (current.length !== 1) return;

      if (!sameMediaIds(current[0], next)) return;

      next.remove(); // 官方拿不到新图册 → 不 replaceWith → 图册保持不动
    })
    .catch(() => {
      /* 请求被中止 / 失败:官方自己处理,这里什么都不做 */
    });
}

function mainPicker(el) {
  if (!(el instanceof Element) || !el.matches('variant-picker')) return null;
  if (!el.closest('main') || el.closest('product-card, quick-add-dialog')) return null;
  return el.dataset.templateProductMatch === 'true' ? el : null;
}

/** 图册里按顺序排的 media-id(只取主图区:slideshow;没有时退到 grid),不含放大弹窗里那份 */
function mediaIds(gallery) {
  const kids = Array.from(gallery.children);
  const root =
    kids.find((kid) => kid.localName === 'slideshow-component') ||
    kids.find((kid) => kid.classList.contains('media-gallery__grid')) ||
    gallery;
  return Array.from(root.querySelectorAll('.product-media[data-media-id]'), (el) => el.dataset.mediaId || '').filter(
    Boolean
  );
}

function sameMediaIds(a, b) {
  const x = mediaIds(a);
  const y = mediaIds(b);
  if (!x.length || x.length !== y.length) return false;
  return x.every((id, i) => id === y[i]);
}
